"use client";

import { usePathname } from "next/navigation";
import { useTranslation } from "react-i18next";
import Link from "@/app/components/LocalizedLink";
import { removeLocaleFromPath } from "@/app/lib/i18n-config";
import { ORBIT_EASE } from "./orbitButtons";

/** Focused flows that bring their own bottom controls: the job editor and
 *  applicants (under /dashboard/job/) and the create-job stepper. */
const HIDDEN_ON = ["/dashboard/job/", "/dashboard/tutorial"];

const TABS = [
  {
    href: "/dashboard",
    labelKey: "navbar.dashboard",
    icon: "M3 11 12 3l9 8v10h-6v-6H9v6H3z",
  },
  {
    href: "/open-positions",
    labelKey: "navbar.openPositions",
    icon: "M3 9a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zM9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2M3 13h18",
  },
  {
    href: "/profile",
    labelKey: "navbar.profile",
    icon: "M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21a8 8 0 0 1 16 0",
  },
];

/**
 * Phone tab bar for signed-in users: a floating glass pill whose active tab
 * is a raised capsule with its label, the others icon-only, ending in a
 * cobalt disc that starts a new job. The chat button (CustomChatButton) takes
 * the disc slot beside the pill; the page reserves room for both in
 * app/styles/tailwind.css (data-tab-bar).
 */
export function MobileTabBar() {
  const { t } = useTranslation();
  const pathname = removeLocaleFromPath(usePathname() || "/");

  if (HIDDEN_ON.some(path => pathname.startsWith(path))) return null;

  return (
    <nav
      data-tab-bar=""
      aria-label={t("navbar.appNav")}
      className="fixed inset-x-0 bottom-0 z-30 flex items-center gap-2 px-4 pb-[calc(0.75rem+env(safe-area-inset-bottom))] lg:hidden"
    >
      <div
        data-lg-bar=""
        className="glass-pill glass-lens flex h-16 min-w-0 flex-1 items-center gap-1 p-1.5"
      >
        {TABS.map(tab => {
          const active = pathname.startsWith(tab.href);
          const label = t(tab.labelKey);
          return (
            <Link
              key={tab.href}
              href={tab.href}
              aria-current={active ? "page" : undefined}
              aria-label={active ? undefined : label}
              className={`flex h-full min-w-11 items-center justify-center gap-1.5 rounded-full transition-colors duration-300 ${ORBIT_EASE} ${
                active
                  ? "glass-raised flex-none px-3.5 text-signal-deep"
                  : "flex-1 text-signal-ink hover:bg-white/40"
              }`}
            >
              <svg
                aria-hidden="true"
                className="h-5 w-5 flex-shrink-0"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d={tab.icon} />
              </svg>
              {active && (
                <span className="max-w-[6.5rem] truncate text-[13px] font-semibold">
                  {label}
                </span>
              )}
            </Link>
          );
        })}
        <Link
          href="/dashboard/tutorial/step-2"
          aria-label={t("dashboard.header.createJob", "Neues Inserat")}
          className={`btn-glass-primary aspect-square h-full flex-none transition-colors duration-300 ${ORBIT_EASE}`}
        >
          <svg
            aria-hidden="true"
            className="h-6 w-6"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.2}
            strokeLinecap="round"
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
        </Link>
      </div>
    </nav>
  );
}
